import { useEffect, useRef, useState } from 'react';
import { Shield } from 'lucide-react';

interface MembersOnlyModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: 'guest' | 'paused';
}

export default function MembersOnlyModal({ isOpen, onClose, reason = 'guest' }: MembersOnlyModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
      closeRef.current?.focus();
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-labelledby="members-only-title"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center mb-4">
          <Shield className="text-navy" size={22} />
        </div>
        <h2 id="members-only-title" className="text-2xl font-bold text-navy">
          {reason === 'paused' ? 'Limited access' : 'Members only'}
        </h2>
        <p className="mt-3 text-gray-600">
          {reason === 'paused'
            ? 'Your app access is limited, so this area is unavailable. Ask your facilitator to restore it. You can still use Home, your Snapshot, this month’s Library, and your Forum’s next meeting.'
            : 'This part of Iron Forums is for members. Ask your facilitator about joining.'}
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="mt-6 px-5 py-2.5 bg-navy text-white rounded-lg font-medium hover:bg-navy-dark transition"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
